/**
 * StashThumbnailMasonry - Masonry layout for StashThumbnail items
 *
 * Distributes items across columns using height estimation for balanced layout.
 * Supports edit mode (multi-select) and custom overlays per item.
 *
 * For custom overlays (processing indicators, tag badges, etc.), use:
 * - getItemOverlay: Returns ReactNode for each post
 * - getItemClassName: Returns additional classes for each post
 *
 * These work with ThumbnailOverlayBadge for consistent styling.
 */

import { memo, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useMasonryColumns, useDistributeToColumns } from "@/hooks/useMasonryColumns";
import { usePreloadStashes } from "@/hooks/usePreloadStashes";
import { StashThumbnail, type StashThumbnailData } from "./StashThumbnail";
import { buildThumbnailItemProps, type ThumbnailSelectProps } from "./types";
import "./StashGallery.css";
import { CTA, META } from "@/lib/typography";

export interface StashThumbnailMasonryProps extends ThumbnailSelectProps {
  posts: StashThumbnailData[];
  className?: string;
  emptyMessage?: string;
  /** Extra node placed after the items in the shortest column (e.g. CTA card) */
  appendNode?: ReactNode;
}

/**
 * Height estimation for masonry distribution.
 * Uses heuristics based on media presence and title length
 * to estimate relative item heights for column balancing.
 */
const estimateItemHeight = (item: StashThumbnailData): number => {
  let height = 1;
  const hasMedia = item.content?.media && item.content.media.length > 0;
  if (hasMedia) height += 3;
  const displayTitle = item.user_title || item.title;
  if (displayTitle && displayTitle.length > 0) {
    height += Math.ceil(displayTitle.length / 40);
  }
  return height;
};


export const StashThumbnailMasonry = memo(
  ({
    posts,
    className,
    emptyMessage = "No items to display",
    appendNode,
    isEditMode,
    selectedIds = new Set(),
    onToggleSelect,
    unavailableIds,
    onRetryItem,
    getItemFooter,
    getItemOverlay,
    getItemClassName,
    collectionContext,
  }: StashThumbnailMasonryProps) => {
    usePreloadStashes(posts);
    const columnCount = useMasonryColumns();
    const columns = useDistributeToColumns(posts, columnCount, estimateItemHeight);
    const shortestColumn = columns.reduce(
      (shortest, column, index) => (column.length < columns[shortest].length ? index : shortest),
      0,
    );

    if (posts.length === 0) {
      return (
        <div className="flex h-full w-full items-center justify-center p-4">
          <div className={cn(META, "text-center")}>{emptyMessage}</div>
        </div>
      );
    }

    return (
      <div className={cn("stash-gallery stash-gallery--masonry", className)}>
        {columns.map((columnItems, colIndex) => (
          <div key={colIndex} className="stash-gallery-column">
            {columnItems.map(post => (
              <StashThumbnail
                key={post.id}
                post={post}
                width="full"
                cornerRadius="2xl"
                hoverScale={true}
                useNaturalAspectRatio={true}
                {...buildThumbnailItemProps(post, {
                  isEditMode,
                  selectedIds,
                  onToggleSelect,
                  unavailableIds,
                  onRetryItem,
                  getItemFooter,
                  getItemOverlay,
                  getItemClassName,
                  collectionContext,
                })}
              />
            ))}
            {colIndex === shortestColumn && appendNode}
          </div>
        ))}
      </div>
    );
  },
);

StashThumbnailMasonry.displayName = "StashThumbnailMasonry";
